import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper py-12 px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <span className="font-serif text-2xl text-ink">
              Cape Town <em className="italic">Monthly</em>
            </span>
          </Link>
          <h1 className="font-serif text-4xl text-ink mb-2" data-testid="forgot-password-title">
            Forgot <em className="italic">password?</em>
          </h1>
          <p className="text-muted caps">Enter your email — we&apos;ll send you a reset link</p>
        </div>

        <div className="bg-paper border border-line-strong p-8">
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-password-sent">
              <div className="font-serif text-2xl mb-3">Check your inbox</div>
              <p className="text-muted mb-6">
                If an account exists for <strong>{email}</strong>, we&apos;ve sent a reset link. Check your inbox (and spam folder) — the link expires in one hour.
              </p>
              <Link to="/login" className="btn-editorial btn-solid inline-flex" data-testid="forgot-back-to-login">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <Alert variant="destructive" className="rounded-none" data-testid="forgot-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="caps text-ink">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="forgot-email-input"
                  className="rounded-none border-gray-400 bg-paper focus-visible:ring-ink"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-ink hover:bg-black text-paper rounded-none caps py-6"
                data-testid="forgot-submit-btn"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
              <div className="text-center pt-2">
                <Link to="/login" className="text-sm text-ink hover:underline font-serif italic" data-testid="forgot-back-link">
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
